import { supabase } from "@/integrations/supabase/client";
import type { FeedPost } from "@/components/post-card";

const POST_SELECT = `
  id, title, content, category, post_type, image_url, location_name,
  upvotes, comment_count, view_count, created_at, severity, status,
  latitude, longitude, repost_of_id, cluster_id,
  author:profiles!posts_author_profile_fkey ( id, username, full_name, avatar_url, account_type, verified, is_founder ),
  community:communities ( id, name, slug ),
  media:post_media ( id, url, media_type, position, poster_url, duration_seconds, review_state )
`;

export type FeedSort = "latest" | "trending" | "discussed";

/**
 * Retry wrapper for flaky/slow networks: 3 attempts with exponential backoff.
 * Only transport-level failures are retried — a Postgres/RLS error is final.
 */
export async function withRetry<T>(fn: () => Promise<T>, attempts = 3): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const msg = err instanceof Error ? err.message : String(err);
      const transient = /fetch|network|timeout|aborted|failed to fetch|502|503|504/i.test(msg);
      if (!transient || i === attempts - 1) throw err;
      await new Promise((r) => setTimeout(r, 400 * 2 ** i));
    }
  }
  throw lastErr;
}

type EngagementRow = {
  post_id: string;
  verify_count: number;
  follower_count: number;
  solution_count: number;
  repost_count: number;
  my_support: boolean;
  my_verify: boolean;
  my_follow: boolean;
  my_save: boolean;
  my_repost: boolean;
};

/**
 * One round trip for all engagement counts + the viewer's own state.
 * Replaces the previous fan-out of ~9 parallel table queries per page.
 */
async function attachEngagement(posts: FeedPost[]) {
  if (!posts.length) return;
  const ids = posts.map((p) => p.id);

  // Counts are nice-to-have: never let them block or fail the feed render.
  let rows: EngagementRow[] = [];
  try {
    const { data, error } = await withRetry(async () =>
      (supabase.rpc as any)("feed_engagement", { _post_ids: ids })
    );
    if (error) throw error;
    rows = (data ?? []) as EngagementRow[];
  } catch {
    rows = [];
  }

  const byId = new Map(rows.map((r) => [r.post_id, r]));

  posts.forEach((p) => {
    const e = byId.get(p.id);
    p.support_count = p.upvotes ?? 0;
    p.verify_count = e?.verify_count ?? 0;
    p.follower_count = e?.follower_count ?? 0;
    p.solution_count = e?.solution_count ?? 0;
    p.repost_count = e?.repost_count ?? 0;
    p.my_support = e?.my_support ?? false;
    p.my_verify = e?.my_verify ?? false;
    p.my_follow = e?.my_follow ?? false;
    p.my_save = e?.my_save ?? false;
    p.my_repost = e?.my_repost ?? false;
  });
}

export async function fetchFeed(opts: {
  sort?: FeedSort;
  communityId?: string;
  category?: string;
  userId?: string;
  authorId?: string;
  /** Restrict to posts by these authors (used by the "Your feed" following view). */
  authorIds?: string[];
  /** Restrict to posts in these communities. */
  communityIds?: string[];
  postTypes?: string[];
  ids?: string[];
  limit?: number;
  cursor?: string | null; // ISO created_at — fetch strictly older than this (latest sort)
  offset?: number; // page offset — used by ranked sorts where a time cursor would skip rows
} = {}): Promise<FeedPost[]> {
  const limit = opts.limit ?? 20;
  const ranked = opts.sort === "trending" || opts.sort === "discussed";

  const run = async () => {
    let q = supabase.from("posts").select(POST_SELECT).eq("hidden", false);

    if (opts.communityId) q = q.eq("community_id", opts.communityId);
    if (opts.category) q = q.eq("category", opts.category as any);
    if (opts.authorId) q = q.eq("author_id", opts.authorId);
    if (opts.postTypes?.length) q = q.in("post_type", opts.postTypes as any);

    // "Your feed": posts from the people you follow, the communities you joined,
    // or both. An empty source set means there is genuinely nothing to show.
    if (opts.authorIds || opts.communityIds) {
      const authors = opts.authorIds ?? [];
      const communities = opts.communityIds ?? [];
      if (!authors.length && !communities.length) return [] as FeedPost[];
      const clauses: string[] = [];
      if (authors.length) clauses.push(`author_id.in.(${authors.join(",")})`);
      if (communities.length) clauses.push(`community_id.in.(${communities.join(",")})`);
      q = clauses.length === 1 ? q.or(clauses[0]!) : q.or(clauses.join(","));
    }
    if (opts.ids) {
      if (!opts.ids.length) return [] as FeedPost[];
      q = q.in("id", opts.ids);
    }


    if (opts.sort === "trending") q = q.order("upvotes", { ascending: false }).order("created_at", { ascending: false });
    else if (opts.sort === "discussed") q = q.order("comment_count", { ascending: false }).order("created_at", { ascending: false });
    else q = q.order("created_at", { ascending: false });

    // Ranked feeds page by offset (a created_at cursor would skip rows because
    // the sort key isn't time); the chronological feed uses a keyset cursor.
    if (ranked) {
      const from = opts.offset ?? 0;
      q = q.range(from, from + limit - 1);
    } else {
      if (opts.cursor) q = q.lt("created_at", opts.cursor);
      q = q.limit(limit);
    }

    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as unknown as FeedPost[];
  };

  const posts = await withRetry(run);
  await attachEngagement(posts);
  return posts;
}

export async function fetchMapPosts(): Promise<(FeedPost & { latitude: number; longitude: number })[]> {
  const posts = await withRetry(async () => {
    const { data, error } = await supabase
      .from("posts")
      .select(POST_SELECT)
      .eq("hidden", false)
      .not("latitude", "is", null)
      .not("longitude", "is", null)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw error;
    return (data ?? []) as any as FeedPost[];
  });
  // Map pins don't render engagement bars, so skip the per-post engagement
  // lookup entirely — it would be a 500-row aggregate on every map open.
  posts.forEach((p) => { p.support_count = p.upvotes ?? 0; });
  return posts as any;
}
